import React, {useState} from 'react';
import {Box, CircularProgress, Paper} from '@material-ui/core';
import {useTranslation} from 'react-i18next';
import {useSoilHealthSummary} from 'apps/web-portal-ui/src/app/hooks/useSoilHealthSummary';
import {useMeasurementConversion} from 'apps/web-portal-ui/src/app/hooks/useMeasurementConversion';
import SummaryTable from './SummaryTable';
import ImageViewer from './ImageViewer';
import NoInfoOverlay from '../../../../../components/UI/DashboardPanel/NoInfoOverlay';
import {Props} from './types';
import styled from 'styled-components';

const TableContainer = styled(Paper)`
  height: 100%;
  margin-top: 10px;
  width: 50%;
  border-top-left-radius: 5px;
  border-bottom-left-radius: 5px;
`;

const Container = styled.div`
  margin-top: 10px;
  display: flex;
  flex-direction: row;
  width: 100%;
`;

const VisualisationContainer = styled(Box)`
  margin-top: 10px;
  padding-bottom: 1%;
  padding: 2.5%;
  margin-right: 5px;
  width: 50%;
  max-height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
  background-color: whitesmoke;
  border-top-right-radius: 5px;
  border-bottom-right-radius: 5px;
`;

const SoilHealthSummaryPanel = ({allFarms, activeFarm, selectedFarm, selectedPolygon, isMobile}: Props) => {
  const {t} = useTranslation();
  const {round} = useMeasurementConversion();
  const {nitrogen, phosphorus, potassium, baselineSOC, averageSOC, soilImage, soilHealthVisualisationTitle, soilBlockValue, loadingSoilImages} = useSoilHealthSummary(
    allFarms,
    activeFarm,
    selectedFarm,
    selectedPolygon
  );

  const [zoom, setZoom] = useState(200);

  const rows: [string, string | number | null][] = [
    [t('polygon.info.block.name'), selectedPolygon?.name],
    ['Nitrogen (g/kg)', round(nitrogen, 2) || null],
    ['Phosphorus (mg/kg)', round(phosphorus, 2) || null],
    ['Potassium (mg/kg)', round(potassium, 2) || null],
    [`${t('soil.organic.carbon.info.baselineSoc')} (g/kg)`, round(baselineSOC, 2) || null],
    [`Current ${t('dashboard.summary.average')} SOC (g/kg)`, round(averageSOC, 2) || null],
  ];

  return (
    <Container>
      <TableContainer elevation={1} variant="outlined" square>
        <SummaryTable rows={rows} />
      </TableContainer>
      <VisualisationContainer>
        {loadingSoilImages ? (
          <CircularProgress size={60} />
        ) : soilImage ? (
          <ImageViewer src={soilImage} title={soilHealthVisualisationTitle} zoom={zoom} onZoomChange={setZoom} />
        ) : (
          <NoInfoOverlay name={t('dashboard.summary.visualisation')} />
        )}
      </VisualisationContainer>
    </Container>
  );
};

export default React.memo(SoilHealthSummaryPanel);
